import React from "react";

const listPreview = (items, maxVisible = 3) => {
  const visible = (items || []).slice(0, maxVisible);
  const remaining = Math.max(0, (items || []).length - visible.length);
  return { visible, remaining };
};

const UnsavedEntityDetailChangesDialog = ({
  open,
  summary,
  onStay,
  onLeaveWithoutSaving,
}) => {
  if (!open || !summary?.hasUnsaved) return null;

  const entityName = summary.entityName || "this item";
  const relationshipSections = (summary.sections || []).filter(
    (section) => section.kind !== "attributes"
  );
  const attributeSections = (summary.sections || []).filter(
    (section) => section.kind === "attributes"
  );

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-[80] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onStay?.();
      }}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsaved-entity-detail-title"
        aria-describedby="unsaved-entity-detail-desc"
      >
        <h3
          id="unsaved-entity-detail-title"
          className="text-lg font-semibold text-slate-900"
        >
          Unsaved changes
        </h3>
        <p id="unsaved-entity-detail-desc" className="mt-2 text-sm text-slate-600">
          You have changes on{" "}
          <span className="font-medium text-slate-900">{entityName}</span> that
          have not been saved. Leave now and these edits will be lost.
        </p>

        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-3">
          {attributeSections.length ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                Unsaved attributes
              </p>
              <ul className="mt-2 space-y-2 text-sm text-amber-950">
                {attributeSections.map((section) => {
                  const fieldPreview = listPreview(section.changedFields);
                  return (
                    <li key={section.key}>
                      <p className="font-medium">{section.label}</p>
                      {section.changedFields?.length ? (
                        <p className="mt-0.5 text-xs text-amber-900/90">
                          <span className="font-medium">Changed:</span>{" "}
                          {fieldPreview.visible.join(", ")}
                          {fieldPreview.remaining > 0
                            ? ` and ${fieldPreview.remaining} more`
                            : ""}
                        </p>
                      ) : (
                        <p className="mt-0.5 text-xs text-amber-900/90">
                          Unsaved edits
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {relationshipSections.length ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                Unsaved relationships
              </p>
              <ul className="mt-2 space-y-2 text-sm text-amber-950">
                {relationshipSections.map((section) => {
                  const addPreview = listPreview(section.adds);
                  const removePreview = listPreview(section.removeNames);
                  return (
                    <li
                      key={section.key}
                      className="border-t border-amber-200/80 pt-2 first:border-0 first:pt-0"
                    >
                      <p className="font-medium">{section.label}</p>
                      {section.adds?.length ? (
                        <p className="mt-0.5 text-xs text-amber-900/90">
                          <span className="font-medium">
                            {section.adds.length} to add:
                          </span>{" "}
                          {addPreview.visible.join(", ")}
                          {addPreview.remaining > 0
                            ? ` and ${addPreview.remaining} more`
                            : ""}
                        </p>
                      ) : null}
                      {section.removeNames?.length ? (
                        <p className="mt-0.5 text-xs text-amber-900/90">
                          <span className="font-medium">
                            {section.removeNames.length} to remove:
                          </span>{" "}
                          {removePreview.visible.join(", ")}
                          {removePreview.remaining > 0
                            ? ` and ${removePreview.remaining} more`
                            : ""}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Use the <span className="font-medium">Save</span> button on each
          edited section to keep your changes.
        </p>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onStay}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
          >
            Stay and save
          </button>
          <button
            type="button"
            onClick={onLeaveWithoutSaving}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100"
          >
            Leave without saving
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnsavedEntityDetailChangesDialog;
